import { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './css/overridesquill.css'
import { formats } from './config.js';
import registerBlots from './BlotsRegistration'; // Appelle la fonction pour enregistrer les blots

function normalizeQuillHtml(html = '') {
  if (typeof html !== 'string') {
    return html ?? '';
  }

  return html
    .replace(/<undefined>/gi, '<p>')
    .replace(/<\/undefined>/gi, '</p>');
}

const QuillEditor = ({ content, onChange, readOnly = false, style }) => {
  const quillRef = useRef();

  const normalizedContent = useMemo(
    () => normalizeQuillHtml(content),
    [content]
  );

  const handleChange = useCallback(
    (html, delta, source, editor) => {
      onChange?.(
        normalizeQuillHtml(html),
        delta,
        source,
        editor
      );
    },
    [onChange]
  );

  // Enregistre les blots une seule fois
  useEffect(() => {
    registerBlots();
  }, []);


  useEffect(() => {
    if (!quillRef.current) return; // Vérifie que Quill est bien monté avant d'accéder à son instance
  
    const quill = quillRef.current.getEditor();
    
     if (quill && typeof normalizedContent === 'string') {
      const delta = quill.clipboard.convert(normalizedContent);
      quill.setContents(delta);
    }
  
    return () => {
      if (quill) {
        quill.off(); // Déconnecter tous les écouteurs d'événements
        quill.root.innerHTML = ''; // Nettoyer le contenu pour éviter les erreurs de référence
      }
    };
  }, [normalizedContent]);
  

  const modules = useMemo(() => ({
    toolbar: readOnly ? null : { container: "#toolbar-container" }
  }), [readOnly]);

  const getContentHash = (content) => {
    if (!content) return "empty";
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convertit en entier 32 bits
    }
    return hash.toString();
  };  

  const contentHash = useMemo(
    () => getContentHash(normalizedContent),
    [normalizedContent]
  );

  return (
    <ReactQuill
      ref={quillRef}
      key={`${readOnly}-${contentHash}`} // Force le re-render proprement
      theme="snow"
      value={normalizedContent}
      modules={modules}
      formats={formats}
      onChange={handleChange}
      style={style}
      readOnly={readOnly}
    />
  );
};

export default QuillEditor;